// Ending — the reverent devotional close. Joseph shares a final meal and prayer
// with the Holy Family; the camera draws back over Nazareth; then the cards
// honoring St. Joseph, an educational note, and the way into Free Play.
import { Scene, type Draw, type BitmapFont, type Preload } from '../../engine/webgpu.js';
import { ART, MUSIC, C, CHARSET, FONT_SERIF } from '../config.js';
import { newGame } from '../session.js';
import { play as sfx } from '../audio.js';

interface Card { big?: string; gold?: boolean; lines: string[]; menu?: boolean; }

export class GameOver extends Scene {
  private fBig!: BitmapFont; private fH1!: BitmapFont; private fH2!: BitmapFont;
  private fBody!: BitmapFont; private fSmall!: BitmapFont;
  private vista = -1; private t = 0; private idx = 0; private sel = 0; private cardT = 0;
  private cards: Card[] = [];

  override preload(load: Preload): void { load.image(ART.titleVista); }

  override setup(): void {
    this.fBig = this.game.assets.font({ font: `bold 58px ${FONT_SERIF}`, charset: CHARSET });
    this.fH1 = this.game.assets.font({ font: `bold 34px ${FONT_SERIF}`, charset: CHARSET });
    this.fH2 = this.game.assets.font({ font: `bold 24px ${FONT_SERIF}`, charset: CHARSET });
    this.fBody = this.game.assets.font({ font: `20px ${FONT_SERIF}`, charset: CHARSET });
    this.fSmall = this.game.assets.font({ font: `italic 16px ${FONT_SERIF}`, charset: CHARSET });
    try { this.vista = this.game.assets.framesOf(ART.titleVista); } catch { this.vista = -1; }
    if (!this.game.sound.muted) this.sound.music(MUSIC.theme, { loop: true, volume: 0.45 });

    this.cards = [
      { lines: ['Joseph enters his home as the lamps are lit.', 'Mary and the young Jesus are waiting for him.', 'The workbench outside is quiet.'] },
      { lines: ['They share a simple meal of bread and olives,', 'and then the Holy Family prays together.', 'Nazareth glows beneath the evening sky.'] },
      { big: 'St. Joseph', gold: true, lines: ['Husband of Mary', 'Guardian of Jesus', 'Worker', 'Provider', 'Faithful Servant'] },
      { big: 'Go to Joseph', gold: true, lines: ['In every need, the Church has long said:', '"Ite ad Ioseph" — Go to Joseph,', 'the faithful provider.'] },
      { lines: [
          'St. Joseph is honored in Catholic tradition as the chaste',
          'husband of Mary and the guardian of Jesus. Though few of his',
          'words are recorded, his faithful, hidden work made him patron',
          'of workers, of fathers, of families, and of the Universal Church.',
          'His life teaches that ordinary work, done faithfully and with',
          'love, can be holy.'] },
      { big: 'St. Joseph the Carpenter', gold: true, lines: ['Work faithfully. Love quietly. Serve God.'], menu: true },
    ];
    this.idx = 0; this.sel = 0; this.cardT = 0;
    this.input.bind({ up: ['ArrowUp', 'KeyW'], down: ['ArrowDown', 'KeyS'], choose: ['Space', 'Enter', 'KeyX'] });
    this.input.onTap(() => this.advance());
    sfx(this, 'chapter');
  }

  private advance(): void {
    const card = this.cards[this.idx];
    if (card.menu) return; // menu handled by choose
    if (this.idx < this.cards.length - 1) { this.idx++; this.cardT = 0; sfx(this, 'page'); }
  }

  override update(dt: number): void {
    super.update(dt);
    this.t += dt; this.cardT += dt;
    const k = this.input.keys;
    const card = this.cards[this.idx];
    if (card.menu) {
      if (k.up.pressed || k.down.pressed) { this.sel = 1 - this.sel; sfx(this, 'select'); }
      if (k.choose.pressed) {
        sfx(this, 'chime');
        if (this.sel === 0) { this.gotoPlay({ fresh: false }); }   // Free Play (keeps state, freePlay set)
        else { newGame(); this.gotoTitle(); }
      }
    } else if (k.choose.pressed) this.advance();
  }

  override drawHud(d: Draw): void {
    const w = d.w, h = d.h;
    if (this.vista >= 0) {
      const fs = this.game.assets.frameSize(this.vista);
      const scale = Math.max(w / fs.w, h / fs.h) * 1.05;
      const dw = fs.w * scale, dh = fs.h * scale;
      d.sprite(this.vista, (w - dw) / 2, (h - dh) / 2, { w: dw, h: dh });
    } else d.rect(0, 0, w, h, C.dusk);
    d.rect(0, 0, w, h, C.dusk, 0.55);

    const card = this.cards[this.idx];
    const cx = w / 2;
    const fade = Math.min(1, this.cardT * 1.5);
    let y = h * 0.28;
    if (card.big) {
      d.text(this.fBig, card.big, cx, y, { align: 'center', color: card.gold ? C.goldLight : C.white, alpha: fade });
      y += 84;
    }
    for (let i = 0; i < card.lines.length; i++) {
      const f = Math.min(1, Math.max(0, this.cardT * 1.4 - i * 0.25));
      d.text(card.big ? this.fH2 : this.fH1, card.lines[i], cx, y + i * (card.big ? 34 : 40), { align: 'center', color: C.parchment, alpha: f });
    }

    if (card.menu) {
      const opts = ['Nazareth — Free Play', 'Return to Title'];
      const my = h * 0.66;
      for (let i = 0; i < opts.length; i++) {
        const on = i === this.sel; const yy = my + i * 46;
        const tw = this.fBody.measure(opts[i]) + 60;
        if (on) { d.panel(cx - tw / 2, yy - 6, tw, 40, 'glossy'); d.rect(cx - tw / 2, yy - 6, tw, 40, C.gold, 0.16); }
        d.text(this.fBody, opts[i], cx, yy + 2, { align: 'center', color: on ? C.white : C.parchment });
      }
      d.text(this.fSmall, 'Arrow keys · X to choose', cx, my + 108, { align: 'center', color: C.parchment, alpha: 0.7 });
    } else {
      d.text(this.fSmall, 'Press X to continue', cx, h - 58, { align: 'center', color: C.goldLight, alpha: 0.72 + Math.sin(this.t * 3) * 0.2 });
    }
    d.text(this.fSmall, 'Presented by CatholicShop.com', cx, h - 30, { align: 'center', color: C.goldLight, alpha: 0.85 });
  }
}
